import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Spinner,
  useBreakpointValue,
  Badge,
  Circle,
  Divider
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';

import { validateStudentRUT, formatRUT, validateRUT } from 'services/appointmentBooking';

// Initials from display_name or first+last
const getInitials = (student) => {
  const name = student.first_name
    ? `${student.first_name} ${student.last_name || ''}`.trim()
    : student.display_name || '';
  return name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '')
    .join('');
};

function StudentRUTValidator({
  onStudentValidated,
  loading,
  setLoading,
  error,
  setError
}) {
  const [rut, setRut] = useState('');
  const [rutError, setRutError] = useState('');
  const [validatedStudent, setValidatedStudent] = useState(null);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor   = useColorModeValue('gray.700', 'gray.200');
  const cardBg      = useColorModeValue('green.50', 'green.900');
  const isMobile    = useBreakpointValue({ base: true, md: false });

  const handleRutChange = (e) => {
    setRut(formatRUT(e.target.value));
    setRutError('');
    setError('');
    setValidatedStudent(null);
  };

  const validateRutFormat = (rutValue) => {
    if (!rutValue) { setRutError('RUT es requerido'); return false; }
    if (!validateRUT(rutValue)) { setRutError('RUT no válido. Formato esperado: 12.345.678-9'); return false; }
    setRutError('');
    return true;
  };

  const handleValidateStudent = async () => {
    if (!validateRutFormat(rut)) return;
    setLoading(true);
    setError('');
    try {
      const response = await validateStudentRUT(rut);
      if (response.success && response.data) {
        setValidatedStudent(response.data);
        setTimeout(() => { onStudentValidated(response.data); }, 2500);
      } else {
        setError(response.message || 'No se pudo validar el estudiante');
      }
    } catch (err) {
      setError(err.message || 'Error al validar el estudiante. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && !rutError) handleValidateStudent();
  };

  return (
    <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
      <Box textAlign="center">
        <Text fontSize={isMobile ? 'lg' : 'xl'} fontWeight="bold" color={textColor} mb={2}>
          Ingrese el RUT del Estudiante
        </Text>
        <Text fontSize={isMobile ? 'sm' : 'md'} color="gray.600">
          Para continuar con la reserva, necesitamos validar la información del estudiante
        </Text>
      </Box>

      <VStack spacing={4}>
        <FormControl isInvalid={!!rutError || !!error}>
          <FormLabel fontSize={isMobile ? 'sm' : 'md'} color={textColor}>
            RUT del Estudiante
          </FormLabel>
          <InputGroup size={isMobile ? 'md' : 'lg'}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Ej: 12.345.678-9"
              value={rut}
              onChange={handleRutChange}
              onKeyPress={handleKeyPress}
              isDisabled={loading || !!validatedStudent}
              borderColor={borderColor}
              _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
              autoComplete="off"
              maxLength={12}
            />
          </InputGroup>
          <FormErrorMessage>{rutError || error}</FormErrorMessage>
        </FormControl>

        <Button
          colorScheme="blue"
          size={isMobile ? 'md' : 'lg'}
          onClick={handleValidateStudent}
          isLoading={loading}
          loadingText="Validando..."
          isDisabled={!rut || !!rutError || !!validatedStudent}
          w="full"
          leftIcon={!loading && !validatedStudent ? <SearchIcon /> : undefined}
        >
          {validatedStudent ? 'Estudiante Validado ✓' : 'Validar Estudiante'}
        </Button>
      </VStack>

      {/* ── Tarjeta del estudiante validado ── */}
      {validatedStudent && (
        <Alert status="success" borderRadius="lg" bg={cardBg} border="1px solid" borderColor="green.200" p={4}>
          <VStack align="stretch" spacing={3} w="full">
            {/* Header: avatar + nombre */}
            <HStack spacing={3} align="flex-start">
              <Circle size="44px" bg="green.500" color="white" fontSize="lg" fontWeight="bold" flexShrink={0}>
                {getInitials(validatedStudent)}
              </Circle>
              <VStack align="start" spacing={0} flex="1">
                <Text fontWeight="700" fontSize="md" color="green.800" lineHeight="1.2">
                  {validatedStudent.display_name}
                </Text>
                <Text fontSize="sm" color="green.600">
                  RUT: {validatedStudent.formatted_rut}
                </Text>
              </VStack>
              <AlertIcon color="green.500" mt={1} />
            </HStack>

            {/* Datos del estudiante */}
            {validatedStudent.group_name && (
              <>
                <Divider borderColor="green.200" />
                <HStack spacing={2}>
                  <Text fontSize="xs" color="green.600" fontWeight="600" w="90px">Curso / Grupo:</Text>
                  <Badge colorScheme="blue" fontSize="xs" borderRadius="md">
                    {validatedStudent.group_name}
                  </Badge>
                </HStack>
              </>
            )}

            {/* Aviso de continuación */}
            <Text fontSize="xs" color="green.600" fontStyle="italic">
              Continuando automáticamente...
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Información adicional */}
      {!validatedStudent && (
        <Box border="1px solid" borderColor={borderColor} borderRadius="md" px={4} py={3}>
          <VStack spacing={1} align="start" fontSize="xs" color="gray.600">
            <Text fontWeight="medium" color={textColor} fontSize="sm" mb={1}>Información importante:</Text>
            <Text>• El RUT debe incluir puntos y guión (Ej: 12.345.678-9)</Text>
            <Text>• Solo se pueden reservar horas para estudiantes registrados</Text>
            <Text>• La validación es inmediata y segura</Text>
          </VStack>
        </Box>
      )}

      {loading && (
        <HStack justify="center" py={4}>
          <Spinner size="md" color="blue.500" />
          <Text color="gray.600" fontSize="sm">Consultando información del estudiante...</Text>
        </HStack>
      )}
    </VStack>
  );
}

export default StudentRUTValidator;
